<template>
  <div>
    <div v-if="!loading">
      <b-row>
        <b-col md="8">
          <h2 class="page-title">
            Leave Recipients
            <small id="pageInfo"><i class="nav-icon i-Information"></i></small>
          </h2>
        </b-col>
      </b-row>

      <!-- Popovers/Modals -->
      <b-popover
        :target="`pageInfo`"
        placement="top"
        triggers="hover"
        delay="500"
        :content="pageDescription"
        custom-class="blue-popover"
        class="info-text"
      ></b-popover>

      <b-modal
        v-if="userIsSuperAdmin"
        id="modal-add-recipient"
        centered
        :hide-footer="true"
        :hide-header="true"
        size="lg"
      >
        <div class="modal-addition-only">
          <b-form-input
            v-model="newEmail"
            class="form-control-rounded input-extra-large"
            type="text"
            required
            placeholder="Enter recipient email..."
            @keyup.enter="submitEmail()"
          ></b-form-input>
          <b-button pill variant="success ripple" @click="submitEmail()"
            >Add</b-button
          >
        </div>
      </b-modal>
      <!-- Popovers/Modals End -->

      <b-row md="6" xl="4" class="email-cards">
        <b-card v-for="type in types" :key="type" class="text-left single-card"
          ><h3>{{ type }}</h3>
          <a v-b-modal.modal-add-recipient @click.prevent="selectedType = type"
            ><small :class="`add-btn ${type.toLowerCase()}-button`">
              <i class="nav-icon i-Add"></i></small
          ></a>
          <b-list-group>
            <b-list-group-item
              v-for="recipient in filterLeaveEmailsByType(type)"
              :key="recipient.id"
              class="d-flex justify-content-between align-items-center"
            >
              {{ recipient.user.email }}
              <a
                class="delete"
                @click.prevent="removeEmailModal(recipient.user.email, type)"
                ><b-badge variant="danger" pill class="delete"
                  ><i class="nav-icon "></i></b-badge
              ></a>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </b-row>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"

export default {
  name: "Recipients",
  apollo: {
    recipients: {
      query: require("@/graphql/Leave/Recipient/GetRecipients.gql"),
      update(data) {
        this.loading = false
        data.getLeaveRecipients.push({
          id: "test",
          user: {
            email: "wendy@netgen.co.za"
          },
          type: "Approval"
        })
        return data.getLeaveRecipients
      }
    }
  },
  data() {
    return {
      loading: true,
      types: {
        approval: "Approval",
        request: "Request"
      },
      pageDescription: "Use this page to add/remove leave email recipients",
      newEmail: "",
      selectedType: "",
      reg: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/
    }
  },
  computed: {
    ...mapGetters({
      getCurrentUser: "getCurrentUser",
      getUserByEmail: "getUserByEmail"
    }),
    userIsSuperAdmin() {
      return (
        this.getCurrentUser &&
        this.getCurrentUser.roles &&
        this.getCurrentUser.roles.includes("Super Admin")
      )
    }
  },
  created() {
    //Only Super Admins should be able to view this page
    if (this.userIsSuperAdmin === false) {
      return this.$router.push("/")
    }

    //TESTING
    if (process.env.NODE_ENV == "development")
      this.newEmail = "chris@netgen.co.za"
  },
  methods: {
    /**
     * Gets leave recipients by type
     * @param type
     * @returns {*}
     */
    filterLeaveEmailsByType(type) {
      return this.recipients.filter(x => x.type.includes(type))
    },
    /**
     *
     * @param email
     * @param type
     */
    removeEmailModal(email, type) {
      this.$bvModal
        .msgBoxConfirm(`Remove ${email} from ${type} Recipients list?`, {
          title: "Remove",
          size: "lg",
          buttonSize: "lg",
          okVariant: "danger",
          headerClass: "p-2 border-bottom-0",
          footerClass: "p-2 border-top-0",
          centered: true,
          okTitle: "Remove",
          cancelTitle: "Cancel",
          cancelVariant: "success"
        })
        .then(value => {
          if (value) this.removeEmail(email, type)
        })
        .catch(() => {
          this.$toasted.error("An error occured")
        })
    },
    /**
     *
     * @param email
     * @param type
     * @returns {ToastObject}
     */
    removeEmail(email, type) {
      const user = this.getUserByEmail(email)
      if (!user) return this.$toasted.error("Email not found in our system")

      this.$apollo
        .mutate({
          mutation: require("@/graphql/Leave/Recipient/RemoveRecipient.gql"),
          variables: {
            recipient: {
              type: type,
              userId: user.id
            }
          }
        })
        .then(() => {
          this.$apollo.queries.recipients.refetch()
        })
        .catch(() => {
          this.$toasted.error("There was an error removing the email")
        })
    },
    submitEmail() {
      if (!this.validEmail()) {
        this.$toasted.error("Please enter a valid email")
      } else {
        this.addRecipient()
        this.$bvModal.hide("modal-add-recipient")
      }
    },
    /**
     * Validates email entered
     * @returns {boolean|boolean}
     */
    validEmail() {
      return this.newEmail !== "" && this.reg.test(this.newEmail)
    },
    /**
     * Adds email recipient
     * @returns {Promise<ToastObject>}
     */
    async addRecipient() {
      const user = this.getUserByEmail(this.newEmail)
      if (!user) return this.$toasted.error("Email not found in our system")

      if (
        this.recipients.find(
          x => x.userId == user.id && x.type == this.selectedType
        )
      )
        return this.$toasted.error(
          `User is already a ${this.selectedType} recipient`
        )
      this.$apollo
        .mutate({
          mutation: require("@/graphql/Leave/Recipient/AddRecipient.gql"),
          variables: {
            recipient: {
              type: this.selectedType,
              userId: user.id
            }
          }
        })
        .then(() => {
          this.$apollo.queries.recipients.refetch()
          this.newEmail = ""
        })
        .catch(() => {
          this.$toasted.error("There was an error adding the new email")
        })
    }
  }
}
</script>

<style scoped>
.email-cards {
  margin: 50px 100px;
}
.single-card {
  margin-right: 5%;
  width: 40%;
}

.add-btn {
  position: absolute;
  top: 26px;
  font-size: 100% !important;
  cursor: pointer;
}

.approval-button {
  left: 122px;
}

.request-button {
  left: 115px;
}

.info-text {
  font-size: 130% !important;
}
.modal-addition-only {
  display: flex;
}
.modal-addition-only input {
  margin-right: 15px;
}

.delete {
  cursor: pointer !important;
}
</style>
<style>
.modal-content {
  background: whitesmoke !important;
}
</style>
